import './informal-caregiver-invite-form.scss';
import {useContext, useState, useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {SnackBarContextType} from '../../contexts/snack-bar/types';
import {OfficeContext} from '../../contexts/office/context';
import {SnackBarContext} from '../../contexts/snack-bar/context';
import {InviteContactMethod} from '../../enums/invite-contact-method';
import {SubmitButtonState} from '../submit-button/submit-button';
import {OfficeContextType} from '../../contexts/office/types';
import {translations} from '../../i18n/translations';
import {EMAIL_REGEX} from '../../utils/constants/regex';
import {PostInvite, RelationshipInviteDataPostInvite} from '../../backend/requests/post-invite';
import {InviteSenderType} from '../../backend/enums/invite-sender-type';
import {InviteType} from '../../backend/enums/invite-type';
import {RoleOnRelationship} from '../../backend/enums/role-on-relationship';
import {InvitesRepository} from '../../repositories/invites';
import {RelationshipInviteData} from '../../backend/interfaces/relationship-invite-data';
import {ContentPage} from '../content-page/content-page';
import {inviteContactMethods} from '../../utils/data/invite-contact-methods';
import {PhoneNumberInput} from '../phone-number-input/phone-number-input';
import {RouterPrompt} from '../router-prompt/router-prompt';
import FlatInput from '../flat-input/flat-input';
import {FlatSelectDropdown} from '../flat-select-dropdown/flat-select-dropdown';
import {validateMobilePhoneNumber} from '../../utils/functions/validate-mobile-phone-number';
import {Button, Stack, Typography} from '@mui/material';
import {Modal} from '../modal/modal';
import {PageHeader} from '../page-header/page-header';

/**
 * Form to invite someone to be an informal caregiver of a senior.
 *
 * It uses the office context to handle the form state.
 *
 * This form was created taking into account that it will exist under the senior tabs.
 * These tabs will give context about the senior associated with the invite.
 * Later on, if it's necessary, we can extend this component to be applied to more contexts.
 */
export const InformalCaregiverInviteForm = (props: {
  seniorId: string;
  seniorName: string;
  senderType: InviteSenderType;
  senderId: string;
  redirectUrl: string;
}) => {
  let history = useHistory();
  const {t} = useTranslation();
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const officeContext: OfficeContextType = useContext(OfficeContext);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(true);
  const [submitDialogOpened, setSubmitDialogOpened] = useState<boolean>(false);
  const [dataEntered, setDataEntered] = useState<boolean>(false);
  const [name, setName] = useState<string | undefined>();
  const [nameError, setNameError] = useState<string | undefined>();
  const [showNameError, setShowNameError] = useState<boolean>(false);
  const [email, setEmail] = useState<string | undefined>();
  const [emailError, setEmailError] = useState<string | undefined>();
  const [showEmailError, setShowEmailError] = useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>();
  const [countryCode, setCountryCode] = useState<{
    phone: string;
    code: string;
  }>({
    phone: '+351',
    code: 'PT',
  });
  const [phoneNumberError, setPhoneNumberError] = useState<string | undefined>();
  const [showPhoneNumberError, setShowPhoneNumberError] = useState<boolean>(false);
  const [contactMethod, setContactMethod] = useState<InviteContactMethod>(
    InviteContactMethod.Email
  );
  const [submitting, setSubmitting] = useState<boolean>(false);

  // On mount
  useEffect(() => {
    // Set the submit button state
    officeContext.setInformalCaregiverInviteFormSubmitButtonState(SubmitButtonState.Normal);

    // Clean up
    return () => {
      // Log
      console.debug('🧹 Invite caregiver button state cleaned in office context.');

      // Clear button state
      officeContext.clearInformalCaregiverInviteFormSubmitButtonState();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validation
  useEffect(() => {
    // ----- Fields validation -----
    // Holds validation
    let nameValid: boolean = false;
    let phoneNumberValid: boolean = false;
    let emailValid: boolean = false;

    // Name validation
    if (name && name.trim().length > 0) {
      // No errors
      if (nameError !== undefined) {
        setNameError(undefined);
      }

      // Valid
      nameValid = true;
    } else {
      // Errors
      if (showNameError) {
        setNameError(
          t(translations.informal_caregiver_invite_form.errors.form_validation_invalid_field)
        );
      }

      // Invalid
      nameValid = false;
    }

    if (contactMethod === InviteContactMethod.Email) {
      // Email validation
      if (email && email.trim().length > 0) {
        if (EMAIL_REGEX.test(email)) {
          // No errors
          if (emailError !== undefined) {
            setEmailError(undefined);
          }

          // Valid
          emailValid = true;
        } else {
          // Errors
          if (showEmailError) {
            setEmailError(
              t(translations.informal_caregiver_invite_form.errors.form_validation_invalid_email)
            );
          }

          // Invalid
          emailValid = false;
        }
      } else {
        // Errors
        if (showEmailError) {
          setEmailError(
            t(translations.informal_caregiver_invite_form.errors.form_validation_invalid_field)
          );
        }

        // Invalid
        emailValid = false;
      }
    } else if (contactMethod === InviteContactMethod.MobilePhoneNumber) {
      // Phone number validation
      if (phoneNumber && phoneNumber.trim().length > 0) {
        // Validate the phone number
        const phoneNumberValidation = validateMobilePhoneNumber(
          `${countryCode.phone}${phoneNumber.trim()}`
        );

        if (
          phoneNumberValidation.isMobilePhoneNumber === true &&
          phoneNumberValidation.isValidPhoneNumber === true
        ) {
          // No errors
          if (phoneNumberError !== undefined) {
            setPhoneNumberError(undefined);
          }

          // Valid
          phoneNumberValid = true;
        } else if (
          phoneNumberValidation.isMobilePhoneNumber === false &&
          phoneNumberValidation.isValidPhoneNumber === true
        ) {
          // Errors
          if (showPhoneNumberError) {
            setPhoneNumberError(
              t(
                translations.informal_caregiver_invite_form.errors
                  .form_validation_not_mobile_phone_number
              )
            );
          }

          // Invalid
          phoneNumberValid = false;
        } else {
          // Errors
          if (showPhoneNumberError) {
            setPhoneNumberError(
              t(
                translations.informal_caregiver_invite_form.errors
                  .form_validation_invalid_mobile_phone_number
              )
            );
          }

          // Invalid
          phoneNumberValid = false;
        }
      } else {
        // Errors
        if (showPhoneNumberError) {
          setPhoneNumberError(
            t(translations.informal_caregiver_invite_form.errors.form_validation_invalid_field)
          );
        }

        // Invalid
        phoneNumberValid = false;
      }
    } else {
      // Invalid
      phoneNumberValid = false;
      emailValid = false;
    }
    // ----- Fields validation -----

    // ----- Data entered -----
    if (
      (name && name.trim().length > 0) ||
      (contactMethod === InviteContactMethod.Email && email && email.trim().length > 0) ||
      (contactMethod === InviteContactMethod.MobilePhoneNumber &&
        phoneNumber &&
        phoneNumber.trim().length > 0) ||
      contactMethod !== InviteContactMethod.Email
    ) {
      // Data was entered
      setDataEntered(true);
    } else {
      // No data was entered
      setDataEntered(false);
    }
    // ----- Data entered -----

    // ----- Submit button validation -----
    if (contactMethod === InviteContactMethod.Email) {
      if (nameValid && emailValid) {
        // Enable submit button
        setSubmitButtonDisabled(false);
      } else {
        // Disable submit button
        setSubmitButtonDisabled(true);
      }
    } else if (contactMethod === InviteContactMethod.MobilePhoneNumber) {
      if (nameValid && phoneNumberValid) {
        // Enable submit button
        setSubmitButtonDisabled(false);
      } else {
        // Disable submit button
        setSubmitButtonDisabled(true);
      }
    } else {
      // Disable submit button
      setSubmitButtonDisabled(true);
    }
    // ----- Submit button validation -----

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    name,
    contactMethod,
    email,
    phoneNumber,
    countryCode,
    nameError,
    emailError,
    phoneNumberError,
    showNameError,
    showEmailError,
    showPhoneNumberError,
  ]);

  // Send invite
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Update submit button state
      officeContext.setInformalCaregiverInviteFormSubmitButtonState(SubmitButtonState.Loading);

      // Request body
      const requestBody: PostInvite<RelationshipInviteDataPostInvite> = {
        type: InviteType.Relationship,
        senderType: props.senderType,
        senderId: props.senderId,
        data: {
          targetUserId: props.seniorId,
          destinationName: name?.trim(),
          destinationPhoneNumber:
            contactMethod === InviteContactMethod.MobilePhoneNumber
              ? `${countryCode.phone}${phoneNumber?.trim()}`
              : undefined,
          destinationEmail: contactMethod === InviteContactMethod.Email ? email?.trim() : undefined,
          destinationRole: RoleOnRelationship.Contact,
        },
      };

      // Send invite
      await InvitesRepository.instance.createInvite<RelationshipInviteData>(requestBody);

      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.informal_caregiver_invite_form.success_message, {
          name: name,
        }),
      });
      setSubmitDialogOpened(false);
      setSubmitting(false);
      // Wait some time just to make sure the button state was updated
      // so the router prompt won't appear when the history is updated.
      setTimeout(() => {
        // Redirect
        history.push(props.redirectUrl);
      }, 500);
    } catch (error) {
      console.debug('🔴 Could not send invite.', error);
      setSubmitDialogOpened(false);
      setSubmitting(false);
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.informal_caregiver_invite_form.errors.error_message, {
          name: name,
        }),
      });

      // Update submit button state
      officeContext.setInformalCaregiverInviteFormSubmitButtonState(SubmitButtonState.Error);
    }
  };

  // Handle change contact method
  const handleChangeContactMethod = (value?: string) => {
    if (value) {
      // Change contact method
      setContactMethod(value as InviteContactMethod);

      // Reset email, phone number, and country calling code
      setEmail(undefined);
      setPhoneNumber(undefined);
      setCountryCode({
        phone: '+351',
        code: 'PT',
      });
    }
  };

  return (
    <ContentPage>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitDialogOpened(true);
        }}
      >
        {props.senderType === 'system' && (
          <PageHeader
            title={t(translations.informal_caregiver_invite_form.title)}
            isSmallTitle={true}
          />
        )}
        <Stack flexDirection="row" className="informal-caregiver-invite-form-field">
          <FlatInput
            type="text"
            errors={nameError}
            required
            disabled={
              officeContext.state.informalCaregiverInviteFormSubmitButtonState ===
              SubmitButtonState.Loading
            }
            value={name || ''}
            label={t(translations.informal_caregiver_invite_form.name_input)}
            onBlur={() => {
              if (!showNameError) {
                setShowNameError(true);
              }
            }}
            onChange={(e) => setName(e.currentTarget.value)}
          />
        </Stack>
        <Stack flexDirection="row" className="informal-caregiver-invite-form-field">
          <FlatSelectDropdown
            label={t(translations.informal_caregiver_invite_form.contact_method_input)}
            disabled={
              officeContext.state.informalCaregiverInviteFormSubmitButtonState ===
              SubmitButtonState.Loading
            }
            required
            value={{
              key: contactMethod,
              label: t(
                inviteContactMethods.find((obj) => obj.type === contactMethod)?.translation || ''
              ),
            }}
            options={inviteContactMethods.map((obj) => {
              return {label: t(obj.translation), key: obj.type};
            })}
            onChange={(option) => {
              handleChangeContactMethod(option.key);
            }}
          />
        </Stack>
        {contactMethod === InviteContactMethod.Email && (
          <Stack flexDirection="row" className="informal-caregiver-invite-form-field">
            <FlatInput
              label={t(translations.informal_caregiver_invite_form.email_input)}
              value={email || ''}
              onChange={(e) => setEmail(e.currentTarget.value)}
              onBlur={() => {
                if (!showEmailError) {
                  setShowEmailError(true);
                }
              }}
              disabled={
                officeContext.state.informalCaregiverInviteFormSubmitButtonState ===
                SubmitButtonState.Loading
              }
              required
              type="email"
            />
          </Stack>
        )}
        {contactMethod === InviteContactMethod.MobilePhoneNumber && (
          <Stack flexDirection="row" className="informal-caregiver-invite-form-field">
            <PhoneNumberInput
              label={t(translations.informal_caregiver_invite_form.mobile_phone_number_input)}
              countryCode={countryCode}
              phoneNumber={phoneNumber || ''}
              handleChangeCountryCode={(value: {phone: string; code: string}) => {
                setCountryCode(value);
              }}
              handleChangePhoneNumber={(value) => setPhoneNumber(value)}
              handleBlur={() => {
                if (!showPhoneNumberError) {
                  setShowPhoneNumberError(true);
                }
              }}
              error={phoneNumberError}
              disabled={
                officeContext.state.informalCaregiverInviteFormSubmitButtonState ===
                SubmitButtonState.Loading
              }
              required={true}
            />
          </Stack>
        )}
        <Stack
          className="informal-caregiver-invite-form-footer"
          flexDirection="row"
          gap="10px"
          justifyContent="space-between"
        >
          <Button
            variant="text"
            color="error"
            sx={{
              width: '100%',
              height: '40px',
              padding: '8px 16px',
              borderRadius: '5px',
              maxWidth: '252px',
            }}
            onClick={() => history.push(props.redirectUrl)}
          >
            {t(translations.user_form.cancel_button)}
          </Button>

          <Button variant="contained" disabled={submitButtonDisabled} type="submit">
            {t(translations.informal_caregiver_invite_form.save_button)}
          </Button>
        </Stack>
      </form>
      <Modal
        open={submitDialogOpened}
        onClose={() => setSubmitDialogOpened(false)}
        maxWidth="sm"
        fullWidth
        className="informal-caregiver-invite-form-modal"
        preventCloseClick
        title=""
        actions={[
          {
            label: t(translations.modal.cancel),
            type: 'destructive',
            onClick: () => setSubmitDialogOpened(false),
          },
          {
            label: t(translations.informal_caregiver_invite_form.save_dialog_action_save_button),
            type: 'nondestructive',
            onClick: handleSubmit,
            loading: submitting,
          },
        ]}
      >
        <Typography className="title">
          {t(translations.informal_caregiver_invite_form.save_dialog_title, {
            name: name,
          })}
        </Typography>
        <Typography className="subtitle">
          {t(translations.informal_caregiver_invite_form.save_dialog_message, {
            name: name,
            senior: props.seniorName,
          })}
        </Typography>
      </Modal>
      <RouterPrompt
        when={
          dataEntered &&
          officeContext.state.informalCaregiverInviteFormSubmitButtonState !==
            SubmitButtonState.Loading
        }
        subtitle={t(translations.informal_caregiver_invite_form.router_prompt_title)}
        message={t(translations.informal_caregiver_invite_form.router_prompt_message)}
        onOK={() => true}
        onCancel={() => false}
        toastNotification={{
          severity: 'info',
          message: t(translations.informal_caregiver_invite_form.cancel_toast),
        }}
      />
    </ContentPage>
  );
};
